import { action, computed, observable, runInAction } from 'mobx';
import type {
  ActivityCalendar,
  CurrencyCurrency,
  LanguageCode,
  OptionProduct,
  Product
} from '@linktivity/ars-neo';
import { convertDateToString } from '@link/share/utils';
import { productApi } from '../../../../services/ars';
import {
  INITIAL_MONTHS,
  initialRange,
  mergeByDate,
  nextRange,
  reachesEnd,
  type CalendarDay,
  type DateRange
} from '../state/calendarRange';
import type { OptionCalendars } from '../state/optionAvailability';

export interface CalendarStoreOptions {
  product: Product;
  languageCode: LanguageCode;
  currency: CurrencyCurrency;
  /** The required options, whose calendars to load; empty when none are. */
  requiredOptions: OptionProduct[];
  /** Every option product, required or not. */
  optionProducts: OptionProduct[];
}

type Status = 'idle' | 'loading' | 'ready' | 'failed';

/**
 * Availability, fetched in the browser: the main product's calendar, a page
 * of months at a time, and the required options' calendars.
 *
 * Only loads and holds data; what the data means for the selection is
 * BookingStore's. Each load reports whether it applied anything, so the
 * booking store knows when to bring the selection up to date.
 *
 * From share's `useActivityCalendar` and `useOptionCalendarFetcher`, which
 * kept the calendar in an injected MobX store slot; here it is the page's
 * own.
 */
export class CalendarStore {
  /** The main product's days, as the API returned them. */
  @observable.ref accessor days: CalendarDay[] = [];
  @observable accessor status: Status = 'idle';
  @observable.ref accessor loadedUntil: Date | null = null;
  @observable accessor hasMore = true;
  @observable accessor isLoadingMore = false;
  /** Required options' calendars; null until they have all settled. */
  @observable.ref accessor optionCalendars: OptionCalendars | null = null;
  /**
   * Every option product's calendar, for whether it is sold out; empty until
   * they have all settled. A calendar that fails is left out, and its option
   * is then never shown sold out.
   */
  @observable.ref accessor optionProductCalendars: OptionCalendars = new Map();

  private readonly options: CalendarStoreOptions;
  private optionCalendarsRequested = false;
  private optionProductCalendarsRequested = false;
  private readonly optionCalendarRequests = new Map<
    string,
    Promise<ActivityCalendar>
  >();

  constructor(options: CalendarStoreOptions) {
    this.options = options;
  }

  @computed get isLoading() {
    return this.status === 'idle' || this.status === 'loading';
  }

  @computed get optionCalendarsReady() {
    return (
      !this.options.requiredOptions.length || this.optionCalendars !== null
    );
  }

  /**
   * The first months. Once per store: resolves false when already started.
   * Throws when the request fails.
   */
  async loadInitial(): Promise<boolean> {
    if (this.status !== 'idle') return false;
    const content = this.options.product.productContent;
    if (!content?.id || !content.activity?.id) {
      runInAction(() => (this.status = 'ready'));
      return true;
    }
    runInAction(() => (this.status = 'loading'));
    const range = initialRange(new Date());
    try {
      const days = await this.fetchDays(range);
      this.applyPage(days, range, false);
      return true;
    } catch (error) {
      runInAction(() => (this.status = 'failed'));
      throw error;
    }
  }

  /**
   * The next months, when the calendar asks. Resolves false when there is
   * nothing more or a load is under way. Throws when the request fails.
   */
  async loadMore(): Promise<boolean> {
    if (!this.hasMore || !this.loadedUntil || this.isLoadingMore) return false;
    runInAction(() => (this.isLoadingMore = true));
    const range = nextRange(this.loadedUntil);
    try {
      const days = await this.fetchDays(range);
      this.applyPage(days, range, true);
      return true;
    } finally {
      runInAction(() => (this.isLoadingMore = false));
    }
  }

  @action private applyPage(
    days: CalendarDay[],
    range: DateRange,
    merge: boolean
  ) {
    this.days = merge ? mergeByDate(this.days, days) : days;
    this.loadedUntil = range.to;
    this.hasMore = reachesEnd(days, range.to);
    this.status = 'ready';
  }

  private async fetchDays({ from, to }: DateRange): Promise<CalendarDay[]> {
    const { product, languageCode, currency } = this.options;
    const res = await productApi.getCalendar({
      languageCode,
      displayCurrency: currency,
      productId: product.id ?? '',
      productContentId: product.productContent?.id ?? '',
      activityId: product.productContent?.activity?.id ?? '',
      targetDateFromYear: from.getFullYear(),
      targetDateFromMonth: from.getMonth() + 1,
      targetDateFromDay: from.getDate(),
      targetDateToYear: to.getFullYear(),
      targetDateToMonth: to.getMonth() + 1,
      targetDateToDay: to.getDate()
    });
    return res?.activityCalendar?.dailyAvailabilities ?? [];
  }

  /**
   * Every required option's calendar. Once per store: resolves false when
   * there are none or it already started. A calendar that fails is left out.
   */
  async loadOptionCalendars(): Promise<boolean> {
    const { requiredOptions } = this.options;
    if (!requiredOptions.length || this.optionCalendarsRequested) return false;
    this.optionCalendarsRequested = true;
    const calendars = await this.fetchOptionCalendars(requiredOptions);
    runInAction(() => (this.optionCalendars = calendars));
    return true;
  }

  /**
   * Every option product's calendar, for the option list. Once per store. The
   * required ones are the same requests as `loadOptionCalendars`'s, and are
   * sent once when the two overlap.
   */
  async loadOptionProductCalendars(): Promise<void> {
    const { optionProducts } = this.options;
    if (!optionProducts.length || this.optionProductCalendarsRequested) return;
    this.optionProductCalendarsRequested = true;
    const calendars = await this.fetchOptionCalendars(optionProducts);
    runInAction(() => (this.optionProductCalendars = calendars));
  }

  private async fetchOptionCalendars(
    options: OptionProduct[]
  ): Promise<OptionCalendars> {
    const results = await Promise.allSettled(
      options.map(async option => {
        const contentId = option.productContent?.id ?? '';
        const activityId = option.productContent?.activity?.id ?? '';
        const calendar = await this.fetchOptionCalendar(contentId, activityId);
        return { contentId, calendar };
      })
    );
    const calendars: OptionCalendars = new Map();
    for (const result of results) {
      if (result.status === 'fulfilled' && result.value.calendar) {
        calendars.set(result.value.contentId, result.value.calendar);
      }
    }
    return calendars;
  }

  /**
   * One option's calendar, for the next months from today. Shared by the
   * loading above and the option cards, which ask for the same calendars;
   * requests for one that overlap are made once, as in the SPA. A later ask
   * fetches again.
   */
  readonly fetchOptionCalendar = (
    productContentId: string,
    activityId: string
  ): Promise<ActivityCalendar | undefined> => {
    if (!productContentId || !activityId) return Promise.resolve(undefined);
    const { product, languageCode, currency } = this.options;
    const from = new Date();
    const to = new Date();
    to.setMonth(to.getMonth() + INITIAL_MONTHS);
    const key = [
      productContentId,
      activityId,
      convertDateToString(from),
      convertDateToString(to)
    ].join(':');
    const pending = this.optionCalendarRequests.get(key);
    if (pending) return pending;
    const request = productApi
      .getCalendar({
        languageCode,
        displayCurrency: currency,
        productId: product.id ?? '',
        productContentId,
        activityId,
        targetDateFromYear: from.getFullYear(),
        targetDateFromMonth: from.getMonth() + 1,
        targetDateFromDay: from.getDate(),
        targetDateToYear: to.getFullYear(),
        targetDateToMonth: to.getMonth() + 1,
        targetDateToDay: to.getDate()
      })
      .then(res => res?.activityCalendar ?? {})
      .finally(() => this.optionCalendarRequests.delete(key));
    this.optionCalendarRequests.set(key, request);
    return request;
  };
}
